import React from 'react'
import styles from './MicrositeAboutSectionForm.module.css'
import TextInput from '../../../components/TextInput/TextInput'
import MicrositeImageGalleryManager from '../../../components/MicrositeImageGalleryManager/MicrositeImageGalleryManager'
import MicrositeImageUploader from '../../../components/MicrositeImageUploader/MicrositeImageUploader'
import MicrositeRepeatableRows from '../../../components/MicrositeRepeatableRows/MicrositeRepeatableRows'

// ──────────────────────────────────────────────────────────────
// Default skeleton matching the real production content shape.
// A store with no saved about-section content yet still gets
// sensible empty defaults to start editing from.
// ──────────────────────────────────────────────────────────────
const DEFAULT_CONTENT = {
    title: '',
    subtitle: '',
    description: '',
    mission: { title: 'Our Mission', text: '' },
    vision: { title: 'Our Vision', text: '' },
    images: [],
    transparentImage: '',
    stats: [],
    features: []
}

const EMPTY_STAT = { number: '', label: '', text: '' }
const EMPTY_FEATURE = { text: '' }

// Backfills any missing nested key from DEFAULT_CONTENT so older or
// partially-saved content (or a brand-new section) still renders
// without crashing on undefined.mission.title etc.
const withDefaults = (content) => ({
    title: content?.title ?? DEFAULT_CONTENT.title,
    subtitle: content?.subtitle ?? DEFAULT_CONTENT.subtitle,
    description: content?.description ?? DEFAULT_CONTENT.description,
    mission: { ...DEFAULT_CONTENT.mission, ...(content?.mission || {}) },
    vision: { ...DEFAULT_CONTENT.vision, ...(content?.vision || {}) },
    images: content?.images || [],
    transparentImage: content?.transparentImage ?? DEFAULT_CONTENT.transparentImage,
    stats: content?.stats || [],
    features: content?.features || []
})

/**
 * MicrositeAboutSectionForm
 * Content editor for the landing page "About us" section, matching
 * the real production content shape: title/subtitle/description,
 * a mission and vision block, a photo gallery, a separate
 * decorative transparent-image asset, a repeatable stats strip
 * (e.g. "10+ Years of excellence"), and a repeatable features list.
 *
 * Pure presentational sub-form — receives `content` and `onChange`
 * from MicrositeLandingSection, which owns loading/saving/versioning.
 * No DB or API changes required: osm_store_config_landing.content is
 * a generic JSON column already; this form just sends the richer
 * object into the same micrositeUpdateLandingSection contract.
 */
const MicrositeAboutSectionForm = ({ content, onChange }) => {
    const safe = withDefaults(content)

    const update = (field, value) => onChange?.({ ...safe, [field]: value })
    const updateMission = (field, value) => onChange?.({ ...safe, mission: { ...safe.mission, [field]: value } })
    const updateVision  = (field, value) => onChange?.({ ...safe, vision: { ...safe.vision, [field]: value } })

    return (
        <section className={styles.fieldGroup}>
            <h2 className={styles.groupTitle}>About us</h2>
            <p className={styles.groupDesc}>Tell visitors your salon's story — what makes you different.</p>

            <TextInput
                label="Title"
                value={safe.title}
                onChange={(v) => update('title', v)}
                placeholder="e.g. Salon Technology Reimagined."
                maxLength={100}
                showCharCount
            />
            <TextInput
                label="Subtitle"
                value={safe.subtitle}
                onChange={(v) => update('subtitle', v)}
                placeholder="e.g. ABOUT LOKACI"
                maxLength={60}
                showCharCount
            />

            <label className={styles.textareaLabel}>Description</label>
            <textarea
                className={styles.textarea}
                rows={5}
                value={safe.description}
                onChange={(e) => update('description', e.target.value)}
                placeholder="Share how your salon started, what you specialize in, and why customers love you…"
            />

            <h3 className={styles.subTitle}>Mission & vision</h3>
            <div className={styles.row2}>
                <div className={styles.nestedFields}>
                    <TextInput
                        label="Mission title"
                        value={safe.mission.title}
                        onChange={(v) => updateMission('title', v)}
                        placeholder="Our Mission"
                    />
                    <label className={styles.textareaLabel}>Mission text</label>
                    <textarea
                        className={styles.textarea}
                        rows={3}
                        value={safe.mission.text}
                        onChange={(e) => updateMission('text', e.target.value)}
                        placeholder="What you're working to deliver for customers"
                    />
                </div>
                <div className={styles.nestedFields}>
                    <TextInput
                        label="Vision title"
                        value={safe.vision.title}
                        onChange={(v) => updateVision('title', v)}
                        placeholder="Our Vision"
                    />
                    <label className={styles.textareaLabel}>Vision text</label>
                    <textarea
                        className={styles.textarea}
                        rows={3}
                        value={safe.vision.text}
                        onChange={(e) => updateVision('text', e.target.value)}
                        placeholder="Where you see the business heading long-term"
                    />
                </div>
            </div>

            <h3 className={styles.subTitle}>Photos</h3>
            <MicrositeImageGalleryManager
                images={safe.images}
                onChange={(v) => update('images', v)}
                addPlaceholder="Paste a photo URL of your salon, team, or work"
            />

            <h3 className={styles.subTitle}>Decorative image (optional)</h3>
            <MicrositeImageUploader
                label="Transparent / cutout image"
                value={safe.transparentImage}
                onChange={(v) => update('transparentImage', v)}
                placeholder="/assets/about-image.png"
                aspectHint="A transparent PNG works best, shown alongside the description"
            />

            <h3 className={styles.subTitle}>Stats strip</h3>
            <p className={styles.groupDesc}>Short highlight numbers, e.g. "10+ Years of excellence".</p>
            <MicrositeRepeatableRows
                items={safe.stats}
                onChange={(v) => update('stats', v)}
                emptyItem={{ ...EMPTY_STAT }}
                addLabel="Add stat"
                renderSummary={(item) => (item.number && item.label) ? `${item.number} ${item.label}` : 'New stat'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.nestedFields}>
                        <div className={styles.row3}>
                            <TextInput
                                label="Number"
                                value={item.number}
                                onChange={(v) => updateItem('number', v)}
                                placeholder="e.g. 10+"
                            />
                            <TextInput
                                label="Label"
                                value={item.label}
                                onChange={(v) => updateItem('label', v)}
                                placeholder="e.g. Years"
                            />
                            <TextInput
                                label="Text"
                                value={item.text}
                                onChange={(v) => updateItem('text', v)}
                                placeholder="e.g. of excellence"
                            />
                        </div>
                    </div>
                )}
            />

            <h3 className={styles.subTitle}>Features list</h3>
            <p className={styles.groupDesc}>Short bullet points, e.g. "Award Winning Stylists".</p>
            <MicrositeRepeatableRows
                items={safe.features}
                onChange={(v) => update('features', v)}
                emptyItem={{ ...EMPTY_FEATURE }}
                addLabel="Add feature"
                renderSummary={(item) => item.text || 'New feature'}
                renderItem={(item, idx, updateItem) => (
                    <div className={styles.nestedFields}>
                        <TextInput
                            label="Text"
                            value={item.text}
                            onChange={(v) => updateItem('text', v)}
                            placeholder="e.g. Award Winning Stylists"
                        />
                    </div>
                )}
            />
        </section>
    )
}

export default MicrositeAboutSectionForm